import {
  generateResellerInvoice,
  getInvoices,
  getResellerInvoices,
  generateCompanyInvoice,
  sendInvoiceToFortnox,
  sendResellerInvoiceToFortnox,
} from '../../api/admin';
import { Currency } from '../../constants/currency';
import { Invoice } from '../../model/Invoice';
import { Money } from '../../utils/currency';
import invalidationManager from './InvalidationManager';

const { hook: useInvoicesQuery } = invalidationManager.createFetcher({
  queryKey: (companyId: string) => ['company', companyId, 'invoices'],
  fetchFunction: async (companyId: string): Promise<Invoice[]> => {
    const invoices = await getInvoices(companyId);

    // Parse ISO string dates into Date objects
    return invoices.map((invoice) => ({
      ...invoice,
      createdDate: new Date(invoice.createdDate),
      invoiceDate: new Date(invoice.invoiceDate),
      invoiceRows: invoice.invoiceRows.map((row) => ({
        ...row,
        unitPrice: new Money(row.unitPrice, Currency.SEK),
      })),
    }));
  },
});

const { hook: useResellerInvoicesQuery } = invalidationManager.createFetcher({
  queryKey: (resellerId: string) => ['reseller', resellerId, 'invoices'],
  fetchFunction: async (resellerId: string): Promise<Invoice[]> => {
    const invoices = await getResellerInvoices(resellerId);

    // Parse ISO string dates into Date objects and handle Money objects
    return invoices.map((invoice) => ({
      ...invoice,
      createdDate: new Date(invoice.createdDate),
      invoiceDate: new Date(invoice.invoiceDate),
      invoiceRows: invoice.invoiceRows.map((row) => ({
        ...row,
        unitPrice: new Money(row.unitPrice, Currency.SEK),
      })),
    }));
  },
});

const useGenerateResellerInvoiceMutation = invalidationManager.createMutationHook({
  mutationFunction: async (resellerId: string) => {
    await generateResellerInvoice(resellerId);
    await invalidationManager.invalidate(['reseller', resellerId, 'invoices']);
  },
});

const useSendResellerInvoiceToFortnoxMutation = invalidationManager.createMutationHook({
  mutationFunction: async (resellerId: string, invoiceId: string) => {
    await sendResellerInvoiceToFortnox(resellerId, invoiceId);
    await invalidationManager.invalidate(['reseller', resellerId, 'invoices']);
  },
});

const useGenerateCompanyInvoiceMutation = invalidationManager.createMutationHook({
  mutationFunction: async (companyId: string, month: string) => {
    await generateCompanyInvoice(companyId, month);
    await invalidationManager.invalidate(['company', companyId, 'invoices']);
  },
});

const useSendInvoiceToFortnoxMutation = invalidationManager.createMutationHook({
  mutationFunction: async (companyId: string, invoiceId: string) => {
    await sendInvoiceToFortnox(companyId, invoiceId);
    await invalidationManager.invalidate(['company', companyId, 'invoices']);
  },
});

export {
  useInvoicesQuery,
  useResellerInvoicesQuery,
  useGenerateResellerInvoiceMutation,
  useGenerateCompanyInvoiceMutation,
  useSendInvoiceToFortnoxMutation,
  useSendResellerInvoiceToFortnoxMutation,
};
